import { type ApiSetting, type Enum, EnumValueType } from '@Model';
import { Checkbox, HtmlEditor, TextInputBase, useStateFromProps } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { useTransparentRef } from '@Hooks/useTransparentRef';

export interface EditSettingProps<TName extends Enum, TGroup extends Enum, TModule extends Enum> {
    setting: ApiSetting<TName, TGroup, TModule>;
    onChange: (setting: ApiSetting<TName, TGroup, TModule>) => void;
    locked: boolean;
}

export function EditSetting<TName extends Enum, TGroup extends Enum, TModule extends Enum>(
    props: EditSettingProps<TName, TGroup, TModule>,
) {
    const [setting, setSetting] = useStateFromProps(props.setting);

    // Freeze the HtmlEditor's value while editable
    const htmlValue = useTransparentRef(setting.value, props.locked);

    const onChange = props.onChange;

    const updateSetting = useCallback(
        (value: string | boolean) => {
            setSetting((setting) => {
                const newSetting = {
                    ...setting,
                    value: value.toString(),
                };
                onChange(newSetting);
                return newSetting;
            });
            return true;
        },
        [setSetting, onChange],
    );

    switch (props.setting.type) {
        case EnumValueType.Html:
            return (
                <HtmlEditor
                    readOnly={props.locked}
                    value={htmlValue.current}
                    onHtmlChange={updateSetting}
                    allowImages
                />
            );
        case EnumValueType.Boolean:
            return (
                <Checkbox
                    value={setting.value === 'true'}
                    label={props.setting.label}
                    onChange={updateSetting}
                    name="SettingCheckbox"
                    locked={props.locked}
                />
            );
        case EnumValueType.Integer:
            return (
                <TextInputBase
                    type={'number'}
                    name="SettingNumber"
                    value={setting.value}
                    label={props.setting.label}
                    onChange={updateSetting}
                    locked={props.locked}
                />
            );
        default:
            return (
                <TextInputBase
                    type={'text'}
                    name="SettingInput"
                    label={props.setting.label}
                    value={setting.value}
                    onChange={updateSetting}
                    locked={props.locked}
                />
            );
    }
}
